import React, { useState } from 'react';

export const LearningDashboard = ({ isOpen, onClose, onLaunchTutorial, userStats }) => {
  const [selectedLevel, setSelectedLevel] = useState('all');

  if (!isOpen) return null;

  // COMPLETE TUTORIAL LIBRARY WITH WORKING NAVIGATION
  const tutorials = [
    {
      id: 'options_fundamentals',
      title: 'Options Trading Fundamentals',
      description: 'Master the basics: contracts, calls vs puts, strikes, premiums',
      difficulty: 'Beginner',
      modules: 5,
      xp: 500,
      icon: '📚',
      color: 'blue',
      status: 'available'
    },
    {
      id: 'risk_management_basics',
      title: 'Risk Management Essentials',
      description: 'Position sizing, stop losses, portfolio protection',
      difficulty: 'Beginner', 
      modules: 4,
      xp: 480,
      icon: '🛡️',
      color: 'red',
      status: 'available'
    },
    {
      id: 'options_spreads',
      title: 'Options Spreads & Combinations',
      description: 'Vertical spreads, iron condors, butterflies, calendars',
      difficulty: 'Intermediate',
      modules: 7,
      xp: 1050,
      icon: '📊',
      color: 'yellow',
      status: 'available'
    },
    {
      id: 'volatility_trading',
      title: 'Volatility Trading Strategies',
      description: 'Straddles, strangles, volatility skew, term structure',
      difficulty: 'Intermediate',
      modules: 6,
      xp: 960,
      icon: '⚡',
      color: 'purple',
      status: 'available'
    },
    {
      id: 'portfolio_hedging',
      title: 'Portfolio Hedging Strategies',
      description: 'Protective puts, collar strategies, index hedging',
      difficulty: 'Intermediate',
      modules: 5,
      xp: 700,
      icon: '🛡️',
      color: 'green',
      status: 'available'
    },
    {
      id: 'advanced_greeks',
      title: 'Advanced Greeks & Risk Metrics',
      description: 'Delta, gamma, theta, vega, rho, complex sensitivities',
      difficulty: 'Advanced',
      modules: 8,
      xp: 1440,
      icon: '🧮',
      color: 'blue',
      status: 'available'
    },
    {
      id: 'institutional_strategies',
      title: 'Institutional Trading Strategies',
      description: 'Professional strategies used by hedge funds and market makers',
      difficulty: 'Expert',
      modules: 10,
      xp: 2000,
      icon: '🏛️',
      color: 'indigo',
      status: 'available'
    },
    {
      id: 'algorithmic_options',
      title: 'Algorithmic Options Trading',
      description: 'Automated trading systems, backtesting, risk management',
      difficulty: 'Expert',
      modules: 9,
      xp: 1980,
      icon: '🤖',
      color: 'cyan',
      status: 'available'
    },
    {
      id: 'exotic_options',
      title: 'Exotic Options & Derivatives',
      description: 'Barrier options, Asian options, binary options',
      difficulty: 'Expert',
      modules: 7,
      xp: 1330,
      icon: '🌟',
      color: 'pink',
      status: 'available'
    }
  ];

  // WORKING LEVEL FILTER FUNCTIONALITY
  const filteredTutorials = tutorials.filter(tutorial => {
    if (selectedLevel === 'all') return true;
    return tutorial.difficulty.toLowerCase() === selectedLevel.toLowerCase();
  });

  // FIXED TUTORIAL LAUNCH HANDLER
  const handleTutorialLaunch = (tutorialId) => {
    console.log('🎓 LAUNCHING TUTORIAL:', tutorialId);
    // Call the parent handler to properly navigate to tutorial
    onLaunchTutorial(tutorialId);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400 bg-green-500/20';
      case 'Intermediate': return 'text-yellow-400 bg-yellow-500/20';
      case 'Advanced': return 'text-orange-400 bg-orange-500/20';
      case 'Expert': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getProgressPercentage = (tutorialId) => {
    const completed = userStats.completed_tutorials || [];
    return completed.includes(tutorialId) ? 100 : 0;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-atticus-navy-900 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto border border-atticus-navy-700">
        {/* HEADER */}
        <div className="flex items-center justify-between p-6 border-b border-atticus-navy-700">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-4">
              <span className="text-white font-bold text-xl">🎓</span>
            </div>
            <div>
              <h2 className="text-white text-2xl font-bold">Learning Dashboard</h2>
              <p className="text-gray-400">Master options trading with comprehensive tutorials</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl font-bold transition-colors"
          >
            ✕
          </button>
        </div>

        {/* USER STATS */}
        <div className="p-6 border-b border-atticus-navy-700 bg-gradient-to-r from-atticus-navy-800 to-atticus-navy-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400">{userStats.total_xp || 0}</div>
              <div className="text-gray-400 text-sm">Total XP</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">{userStats.completed_tutorials?.length || 0}/9</div>
              <div className="text-gray-400 text-sm">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">{userStats.certification_progress || 0}%</div>
              <div className="text-gray-400 text-sm">Certified</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400">{userStats.badges?.length || 0}</div>
              <div className="text-gray-400 text-sm">Badges</div>
            </div>
          </div>
        </div>

        {/* FIXED LEVEL FILTER BUTTONS */}
        <div className="p-6 border-b border-atticus-navy-700">
          <div className="flex flex-wrap gap-2">
            {['all', 'beginner', 'intermediate', 'advanced', 'expert'].map(level => (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedLevel === level
                    ? 'bg-blue-600 text-white'
                    : 'bg-atticus-navy-700 text-gray-300 hover:bg-atticus-navy-600 hover:text-white'
                }`}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
                {level === 'all' && ` (${tutorials.length})`}
                {level !== 'all' && ` (${tutorials.filter(t => t.difficulty.toLowerCase() === level).length})`}
              </button>
            ))}
          </div>
        </div>

        {/* TUTORIALS GRID */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTutorials.map(tutorial => {
              const progress = getProgressPercentage(tutorial.id);
              const isCompleted = progress === 100;
              
              return (
                <div
                  key={tutorial.id}
                  className="bg-atticus-navy-800 rounded-lg p-6 border border-atticus-navy-600 hover:border-blue-500 transition-all transform hover:scale-105"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-4xl">{tutorial.icon}</div>
                    <div className={`px-2 py-1 rounded-full text-xs font-bold ${getDifficultyColor(tutorial.difficulty)}`}>
                      {tutorial.difficulty}
                    </div>
                  </div>

                  <h3 className="text-white text-lg font-bold mb-2">{tutorial.title}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{tutorial.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-500">
                      {tutorial.modules} modules • {tutorial.xp} XP
                    </div>
                    {isCompleted && (
                      <div className="text-green-400 text-sm font-bold">✓ Complete</div>
                    )}
                  </div>

                  {/* PROGRESS BAR */}
                  <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>

                  {/* FIXED LAUNCH BUTTON */}
                  <button
                    onClick={() => handleTutorialLaunch(tutorial.id)}
                    className={`w-full py-3 rounded-lg font-bold transition-all ${
                      isCompleted
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isCompleted ? 'Review Tutorial' : 'Start Tutorial'} →
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* COMPETITION SECTION - COMING SOON */}
        <div className="p-6 border-t border-atticus-navy-700">
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-4xl mr-4">🏆</div>
                <div>
                  <h3 className="text-white text-xl font-bold">Trading Competitions</h3>
                  <p className="text-gray-400">Compete with other traders and win prizes</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg px-4 py-2 mb-2">
                  <span className="text-orange-400 font-bold">🚧 Coming Soon</span>
                </div>
                <p className="text-gray-500 text-sm">Competitions launching soon!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
